// Shared controlled vocabulary for failure-mode tags (drawing-level) and reject
// reasons (instance-level) — one vocabulary, two scopes. Values MUST match
// server/config/failureModes.js exactly; the server validates rejectionReason
// against that list. See PIPELINE_IMPROVEMENT_PLAN.md Phase 0.2 item 5.
// Add new modes in whatever order — DrawingSidePane.js sorts by label for
// display (SORTED_FAILURE_MODES), so this array's order doesn't matter.
//
// `entity` says which reject picker (Element or Connection) offers this mode,
// so the in-app dropdown doesn't force scanning ~20 entries irrelevant to
// what's selected — see server/config/failureModes.js, which enforces the
// same restriction server-side. `entity: null` means "no dedicated
// instance-level trigger exists for this one" (see below) — shown in both
// pickers rather than hidden from both, so nothing already in use becomes
// unreachable.

export const FAILURE_MODES = [
  { value: 'connection_in_element', label: 'Connection in element', entity: 'connection' },
  { value: 'connection_in_text', label: 'Connection in text', entity: 'connection' },
  { value: 'dashed_signal', label: 'Dashed signal line', entity: 'connection' },
  { value: 'dense_notes_panel', label: 'Dense notes panel', entity: 'element' },
  { value: 'element_in_text', label: 'Element in text', entity: 'element' },
  { value: 'false_positive_shapes', label: 'False-positive shape', entity: 'element' },
  { value: 'flow_arrows', label: 'Flow arrows', entity: null },
  { value: 'has_legend', label: 'Has legend', entity: null },
  { value: 'implausible_routing', label: 'Implausible routing', entity: 'connection' },
  { value: 'incomplete_text_line', label: 'Incomplete text line', entity: 'element' },
  { value: 'instrument_outline', label: 'Instrument outline', entity: 'connection' },
  // label_false_positive vs label_wrong_target: distinct mechanisms, kept
  // separate on purpose. A `labels` connection can be correctly *formed* —
  // real nearby text, sensible association — while its target element is
  // itself a false positive (bad shape/OCR detection upstream); that's not an
  // association-logic failure, so it must not count against label_wrong_target
  // (which is specifically "several valid candidates existed, picked the
  // wrong one"). Also why graph-walking to auto-reject anything touching a
  // rejected element doesn't work: a well-formed label/line pointing at a
  // false positive is still a well-formed label/line, not evidence the
  // association itself is wrong — even a connection with false positives at
  // *both* ends may just be two independent bad symbol detections joined by
  // an otherwise-correct line. Confirmed 2026-07-29 while annotating ~800
  // element rejections on one drawing.
  { value: 'label_false_positive', label: 'Label false positive', entity: 'connection' },
  { value: 'label_wrong_target', label: 'Label wrong target', entity: 'connection' },
  { value: 'low_scan_quality', label: 'Low scan quality', entity: null },
  { value: 'orphan_connection', label: 'Orphan connection', entity: 'connection' },
  { value: 'oversized_compound_line', label: 'Oversized compound line', entity: 'connection' },
  // Distinct from false_positive_shapes on purpose — the detected shape isn't
  // hallucinated, it genuinely exists (e.g. Hough correctly finds the disc at
  // the center of a globe valve's >o< symbol), but the pipeline never
  // recognized the surrounding shape as a compound symbol, so the sub-piece
  // is left standing alone as its own low-value element. Same rationale as
  // the label_false_positive/label_wrong_target split above: conflating
  // "wrong mechanism" tags loses the signal needed to fix the actual cause
  // later (here, likely shape-assembly/grouping, not raw shape detection).
  { value: 'partial_compound_shape', label: 'Partial compound shape', entity: 'element' },
  { value: 'rotated_text_misread', label: 'Rotated text misread', entity: 'element' },
  { value: 'stacked_text', label: 'Stacked text', entity: 'element' },
  { value: 'text_in_elements', label: 'Text in element', entity: 'element' },
  { value: 'vessel_outline', label: 'Vessel outline', entity: 'connection' },
  { value: 'false_positive_ocr', label: 'False-positive OCR', entity: 'element' },
  // Set automatically when the element at either end of a names/describes link
  // is rejected (server/config/failureModes.js rejectTextLinksWith).
  { value: 'rejected_target', label: 'Rejected target', entity: 'connection' },
];
